import { useEffect, useState } from "react";
import { Check, Loader2, Trash2, X } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { cn } from "~/lib/utils";

type SaveState = "idle" | "saving" | "saved";

interface ConversationShortcutRowProps {
  index: number;
  label: string;
  url: string;
  saveState: SaveState;
  urlInvalid?: boolean;
  disabled?: boolean;
  onChangeLabel: (value: string) => void;
  onChangeUrl: (value: string) => void;
  onDelete: () => void;
}

const ConversationShortcutRow = ({
  index,
  label,
  url,
  saveState,
  urlInvalid,
  disabled,
  onChangeLabel,
  onChangeUrl,
  onDelete,
}: ConversationShortcutRowProps) => {
  const [showCheck, setShowCheck] = useState(false);

  useEffect(() => {
    if (saveState === "saved") {
      setShowCheck(true);
      const timer = setTimeout(() => setShowCheck(false), 2000);
      return () => clearTimeout(timer);
    }
    setShowCheck(false);
  }, [saveState]);

  return (
    <div className="flex gap-2 items-start">
      <div className="flex-1 space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold">Shortcut {index + 1}</span>
          {saveState === "saving" && (
            <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
          )}
          {showCheck && !urlInvalid && <Check className="size-4 text-green-600" />}
        </div>
        <Input
          placeholder="Label"
          value={label}
          disabled={disabled}
          onChange={(e) => onChangeLabel(e.target.value)}
          maxLength={100}
          className={cn("h-9")}
        />
        <Input
          placeholder="Paste a shared conversation link"
          value={url}
          disabled={disabled}
          onChange={(e) => onChangeUrl(e.target.value)}
          maxLength={2048}
          className={cn("h-9", urlInvalid && "border-destructive")}
        />
        {urlInvalid && (
          <div className="flex items-center gap-1 text-destructive">
            <X className="size-3.5 shrink-0" />
            <span className="text-xs">URLs must point to shared conversations</span>
          </div>
        )}
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="mt-7 size-8"
        onClick={onDelete}
        disabled={disabled}
      >
        <Trash2 className="size-4" />
      </Button>
    </div>
  );
};

export default ConversationShortcutRow;
